import React, { useId, useMemo } from 'react';
import { HOUR_VOLUME, LM_TOKENS } from '../../live-monitor/config';
import type { LiveCall } from '../../live-monitor/types';
import { Icon } from '../Icon/Icon';

export interface LiveMonitorHeroProps {
  calls: LiveCall[];
}

function HeroVolumeCard() {
  const gradId = useId().replace(/:/g, '');
  const data = HOUR_VOLUME;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const w = 280;
  const h = 70;
  const range = max - min || 1;

  const points = useMemo(
    () =>
      data.map((v, i) => {
        const x = (i / (data.length - 1)) * w;
        const y = h - ((v - min) / range) * (h - 8) - 4;
        return [x, y] as const;
      }),
    [data, max, min, range, w, h],
  );

  const linePath = points.map(([x, y]) => `${x},${y}`).join(' ');
  const areaPath = `M ${points[0]![0]},${h} L ${points.map(([x, y]) => `${x},${y}`).join(' L ')} L ${points[points.length - 1]![0]},${h} Z`;

  return (
    <div
      className="lm-kpi-card overflow-hidden rounded-2xl border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]"
      style={{ padding: '16px 18px 18px', display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <div className="flex items-center justify-between">
        <div className="lm-eyebrow text-[10px]">Volume · last hour</div>
        <span className="text-[11px] font-semibold" style={{ color: LM_TOKENS.greenDeep }}>
          ▲ Up 32%
        </span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="lm-tab-num text-[36px] font-semibold leading-none tracking-[-0.03em] text-[var(--ink-0)]">
          54
        </span>
        <span className="ml-1 text-[14px] font-medium text-[var(--ink-2)]">calls</span>
      </div>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="mt-1 block">
        <defs>
          <linearGradient id={gradId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={LM_TOKENS.green} stopOpacity="0.32" />
            <stop offset="100%" stopColor={LM_TOKENS.green} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#${gradId})`} />
        <polyline
          points={linePath}
          fill="none"
          stroke={LM_TOKENS.green}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle
          cx={points[points.length - 1]![0]}
          cy={points[points.length - 1]![1]}
          r="3"
          fill={LM_TOKENS.green}
        />
      </svg>
    </div>
  );
}

export function LiveMonitorHero({ calls }: LiveMonitorHeroProps) {
  const count = calls.length;
  const inbound = calls.filter((c) => c.direction === 'inbound').length;
  const outbound = count - inbound;

  return (
    <section className="lm-fade-up lm-d-0 mb-6">
      <div
        className="grid items-end gap-5"
        style={{ gridTemplateColumns: 'minmax(0, 1fr) 320px' }}
      >
        <div>
          <div className="mb-2 flex items-center gap-[9px]">
            <Icon kind="sparkle" size={14} color="var(--gold)" stroke={2} />
            <span className="lm-eyebrow text-[10px]" style={{ color: LM_TOKENS.brassDeep }}>
              Live monitor
            </span>
          </div>
          <h1
            className="m-0 flex flex-wrap items-baseline gap-4 leading-none tracking-[-0.04em]"
            style={{ fontWeight: 500 }}
          >
            <span
              className="lm-tab-num text-[80px] font-semibold tracking-[-0.045em] text-[var(--text-primary)]"
              style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}
            >
              {count}
            </span>
            <span className="text-[44px] font-medium tracking-[-0.035em] text-[var(--ink-0)]">
              calls right now
            </span>
          </h1>
          <div className="mt-[18px] flex flex-wrap gap-2">
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-[5px] text-[12px] font-semibold tracking-[-0.005em]"
              style={{
                background: LM_TOKENS.greenWash,
                color: LM_TOKENS.greenDeep,
                border: `1px solid ${LM_TOKENS.greenSoft}`,
              }}
            >
              <Icon kind="phone-in" size={12} stroke={2} />
              {inbound} inbound
            </span>
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-[5px] text-[12px] font-semibold tracking-[-0.005em]"
              style={{
                background: LM_TOKENS.brassTint,
                color: LM_TOKENS.brassDeep,
                border: '1px solid rgba(168, 132, 92, 0.18)',
              }}
            >
              <Icon kind="phone-out" size={12} stroke={2} />
              {outbound} outbound
            </span>
          </div>
          <p className="mt-4 max-w-[600px] text-[14px] leading-[1.55] tracking-[-0.005em] text-[var(--ink-2)]">
            Every call happening right now. Click a tile to listen in, escalate, or watch the full
            action timeline.
          </p>
        </div>
        <HeroVolumeCard />
      </div>
    </section>
  );
}
